import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    UserRound,
    Mail,
    Phone,
    ShieldCheck,
    Plus,
    Edit2,
    Building2,
    ChevronDown,
    ChevronUp,
    Unlock,
} from 'lucide-react';
import usersService from '../services/users';
import rolesService from '../services/roles';
import tenantsService from '../services/tenants';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import RoleChecklist from '../components/common/RoleChecklist';
import { splitRoles, mergeRoleIds } from '../utils/roleFlags';

const UsersPage = () => {
    const { apps, loading: loadingApps, error: appsError } = useApps();
    const [appId, setAppId] = useState('');
    const [tenantId, setTenantId] = useState('');
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [expandedTenants, setExpandedTenants] = useState(new Set());
    const [formData, setFormData] = useState({
        username: '',
        first_name: '',
        last_name: '',
        gender: '',
        email_id: '',
        phone_no: '',
        role_ids: [],
    });

    useEffect(() => {
        if (apps && apps.length > 0) {
            const appIdFromQuery = searchParams.get('app_id');
            const tenantIdFromQuery = searchParams.get('tenant_id');
            const defaultAppId = resolveDefaultAppId(apps, appIdFromQuery);
            setAppId(defaultAppId);
            if (tenantIdFromQuery) {
                setTenantId(tenantIdFromQuery);
            }
            loadUsers(defaultAppId, tenantIdFromQuery);
            loadRoles(defaultAppId);
            loadTenants(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    const loadUsers = async (targetAppId, targetTenantId = null) => {
        if (!targetAppId || targetAppId.trim() === '') {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await usersService.getAll(targetAppId.trim(), targetTenantId);
            setUsers(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const loadRoles = async (targetAppId) => {
        if (!targetAppId) return;
        try {
            const data = await rolesService.getAll(targetAppId);
            setRoles(data || []);
        } catch (err) {
            console.error('Failed to load roles:', err);
        }
    };

    const loadTenants = async (targetAppId) => {
        if (!targetAppId) return;
        try {
            const data = await tenantsService.getAll(targetAppId);
            setTenants(data || []);
        } catch (err) {
            console.error('Failed to load tenants:', err);
        }
    };

    const handleAppChange = (e) => {
        const selectedAppId = e.target.value;
        setAppId(selectedAppId);
        setSelectedAppId(selectedAppId);
        setTenantId(''); // Reset tenant when app changes
        if (selectedAppId) {
            loadUsers(selectedAppId, null);
            loadRoles(selectedAppId);
            loadTenants(selectedAppId);
        }
    };

    // Role picks ∪ flags (flags dropped for an admin account); the backend full-replaces.
    const submittedRoleIds = () => {
        // Ids not in the loaded list are kept as roles (never silently dropped).
        const { flags } = splitRoles(roles);
        const isFlag = (id) => flags.some((r) => r.role_id === id);
        const ids = formData.role_ids;
        return mergeRoleIds(
            ids.filter((id) => !isFlag(id)),
            ids.filter(isFlag),
            roles
        );
    };
    const hasPlainRole = () => splitRoles(roles).roles.some((r) => formData.role_ids.includes(r.role_id));

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!appId) {
            setError('App ID is required');
            return;
        }
        setError('');
        try {
            await usersService.createUser(appId, { ...formData, role_ids: submittedRoleIds() }, tenantId || null);
            setSuccessMessage('User created successfully!');
            setShowCreateModal(false);
            resetForm();
            loadUsers(appId, tenantId || null);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create user');
        }
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        if (!appId) {
            setError('App ID is required');
            return;
        }
        if (!selectedUser) return;
        if (!hasPlainRole()) {
            setError('Select at least one role');
            return;
        }
        setError('');
        try {
            await usersService.updateDetails(
                appId,
                { ...formData, username: selectedUser.username, role_ids: submittedRoleIds() },
                tenantId || null
            );
            setSuccessMessage('User updated successfully!');
            setShowUpdateModal(false);
            setSelectedUser(null);
            resetForm();
            loadUsers(appId, tenantId || null);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update user');
        }
    };

    const handleToggleStatus = async (username, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'disable' : 'enable'} this user?`)) return;
        setError('');
        try {
            await usersService.updateStatus(username, currentStatus ? 0 : 1);
            setSuccessMessage(`User ${currentStatus ? 'disabled' : 'enabled'} successfully!`);
            loadUsers(appId, tenantId || null);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update status');
        }
    };

    const handleUnlock = async (username) => {
        setError('');
        try {
            await usersService.unlockAccount(username);
            setSuccessMessage('Account unlocked!');
            loadUsers(appId, tenantId || null);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to unlock account');
        }
    };

    const resetForm = () => {
        setFormData({
            username: '',
            first_name: '',
            last_name: '',
            gender: '',
            email_id: '',
            phone_no: '',
            role_ids: [],
        });
    };

    const openUpdateModal = (user) => {
        const userRoleIds = Array.isArray(user.roles) ? user.roles.map((r) => r.role_id).filter(Boolean) : [];
        setSelectedUser(user);
        // Set tenant_id from the user's data for proper context
        if (user.tenant_id) {
            setTenantId(user.tenant_id);
        }
        setFormData({
            username: user.username || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email_id: user.email_id || '',
            phone_no: user.phone_no || '',
            gender: user.gender || '',
            role_ids: userRoleIds,
        });
        setShowUpdateModal(true);
    };

    // Get current app to check if it's multi-tenant
    const currentApp = apps.find((app) => app.app_id === appId);
    const isMultiTenant = currentApp?.is_multi_tenant === 1 || currentApp?.is_multi_tenant === '1';

    // Create tenant map for quick lookup
    const tenantMap = useMemo(() => {
        const map = new Map();
        tenants.forEach((tenant) => {
            map.set(tenant.tenant_id, tenant.tenant_name);
        });
        return map;
    }, [tenants]);

    // Group users by tenant_id for multi-tenant apps
    const groupedUsers = useMemo(() => {
        if (!isMultiTenant) {
            return { '': users }; // Single group for non-multi-tenant apps
        }

        const grouped = {};
        users.forEach((user) => {
            // Handle null/undefined tenant_id - use empty string as key
            const userTenantId = user.tenant_id || '';
            if (!grouped[userTenantId]) {
                grouped[userTenantId] = [];
            }
            grouped[userTenantId].push(user);
        });

        // Sort tenant groups: null/empty tenant first, then by tenant name
        const sortedGroups = {};
        const tenantIds = Object.keys(grouped).sort((a, b) => {
            if (a === '' && b !== '') return -1;
            if (a !== '' && b === '') return 1;
            if (a === '' && b === '') return 0;
            const nameA = tenantMap.get(a) || '';
            const nameB = tenantMap.get(b) || '';
            return nameA.localeCompare(nameB);
        });

        tenantIds.forEach((tid) => {
            sortedGroups[tid] = grouped[tid];
        });

        return sortedGroups;
    }, [users, isMultiTenant, tenantMap]);

    // Initialize expanded tenants when groupedUsers changes (expand all by default)
    useEffect(() => {
        if (isMultiTenant && Object.keys(groupedUsers).length > 0) {
            const allTenantIds = Object.keys(groupedUsers);
            setExpandedTenants(new Set(allTenantIds));
        }
    }, [groupedUsers, isMultiTenant]);

    // Toggle tenant accordion
    const toggleTenant = (tenantId) => {
        setExpandedTenants((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(tenantId)) {
                newSet.delete(tenantId);
            } else {
                newSet.add(tenantId);
            }
            return newSet;
        });
    };

    // Render user card component
    // A user's roles and flags as display strings (flags are never shown as roles).
    const roleNames = (u) => {
        const { roles: plain, flags } = splitRoles(Array.isArray(u.roles) ? u.roles : []);
        const names = (list) => list.map((r) => r.role_name).join(', ');
        return { roles: names(plain), flags: names(flags) };
    };

    const renderUserCard = (u) => (
        <div
            key={u.username}
            className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm hover:shadow-md transition space-y-3"
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-primary-50 text-primary-700 grid place-content-center shadow-inner">
                        <UserRound className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-base font-semibold text-slate-900">{u.username}</div>
                        <div className="text-sm text-slate-600 flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" /> {u.email_id}
                        </div>
                    </div>
                </div>
                <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                        u.is_user_active
                            ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                            : 'text-amber-700 bg-amber-50 border-amber-100'
                    }`}
                >
                    {u.is_user_active ? 'Active' : 'Disabled'}
                </span>
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-400" />
                {roleNames(u).roles || '—'}
                {roleNames(u).flags && <span className="text-xs text-slate-500">(flags: {roleNames(u).flags})</span>}
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-2">
                <Phone className="h-4 w-4 text-slate-400" />
                {u.phone_no || '—'}
            </div>
            <div className="text-sm text-slate-500">
                {u.first_name} {u.last_name}
            </div>
            <div className="flex gap-2 pt-2 border-t border-slate-100">
                <button
                    onClick={() => openUpdateModal(u)}
                    className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 flex items-center justify-center gap-1"
                >
                    <Edit2 className="h-3 w-3" /> Edit
                </button>
                <button
                    onClick={() => handleToggleStatus(u.username, u.is_user_active)}
                    className={`flex-1 px-2 py-1 rounded-md text-xs font-medium flex items-center justify-center gap-1 ${
                        u.is_user_active
                            ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                            : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                    }`}
                >
                    {u.is_user_active ? 'Disable' : 'Enable'}
                </button>
                <button
                    onClick={() => handleUnlock(u.username)}
                    className="px-2 py-1 rounded-md text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center justify-center gap-1"
                    title="Unlock account"
                >
                    <Unlock className="h-3 w-3" />
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            <PageHeader
                title="Users"
                subtitle={`Manage users for ${appId ? `App: ${appId.substring(0, 8)}...` : 'selected app'}${
                    tenantId ? ` (Tenant: ${tenantId.substring(0, 8)}...)` : ''
                }`}
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name} {app.is_active ? '' : '(Disabled)'}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Add User
                            </button>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : loading ? (
                <SkeletonList />
            ) : error ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">Select an App to view users.</p>
                </div>
            ) : users.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">No users found for the selected app.</p>
                </div>
            ) : isMultiTenant ? (
                // Grouped view for multi-tenant apps (accordion)
                <div className="space-y-3">
                    {Object.entries(groupedUsers).map(([tenantId, tenantUsers]) => {
                        const tenantName = tenantId
                            ? tenantMap.get(tenantId) || `Tenant ${tenantId.substring(0, 8)}...`
                            : 'No Tenant (Non-Multi-Tenant Users)';
                        const isExpanded = expandedTenants.has(tenantId);
                        const tenantKey = tenantId || 'no-tenant';

                        return (
                            <div
                                key={tenantKey}
                                className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm"
                            >
                                {/* Accordion Header */}
                                <button
                                    onClick={() => toggleTenant(tenantId)}
                                    className="w-full flex items-center gap-3 px-4 py-3 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-2 flex-1">
                                        <Building2 className="h-5 w-5 text-purple-600 flex-shrink-0" />
                                        <h3 className="text-lg font-semibold text-purple-900">{tenantName}</h3>
                                        {tenantId && (
                                            <span className="text-xs text-purple-500 font-mono">
                                                ({tenantId.substring(0, 8)}...)
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-purple-600">
                                            {tenantUsers.length} {tenantUsers.length === 1 ? 'user' : 'users'}
                                        </span>
                                        {isExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-purple-600 flex-shrink-0" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-purple-600 flex-shrink-0" />
                                        )}
                                    </div>
                                </button>

                                {/* Accordion Content */}
                                {isExpanded && (
                                    <div className="p-4 border-t border-purple-100">
                                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                                            {tenantUsers.map((u) => renderUserCard(u))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                // Flat view for non-multi-tenant apps
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{users.map((u) => renderUserCard(u))}</div>
            )}

            {/* Create User Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New User" size="lg">
                <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Username *</label>
                            <input
                                type="text"
                                required
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                            <input
                                type="email"
                                required
                                value={formData.email_id}
                                onChange={(e) => setFormData({ ...formData, email_id: e.target.value.toLowerCase() })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                            <input
                                type="text"
                                required
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                            <input
                                type="text"
                                required
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Gender *</label>
                            <select
                                required
                                value={formData.gender}
                                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            >
                                <option value="">Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                            <input
                                type="tel"
                                required
                                value={formData.phone_no}
                                onChange={(e) => setFormData({ ...formData, phone_no: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                    </div>
                    <RoleChecklist
                        roles={roles}
                        selected={formData.role_ids}
                        onChange={(ids) => setFormData((prev) => ({ ...prev, role_ids: ids }))}
                    />
                    <p className="text-xs text-slate-500">Password will be auto-generated and sent via email.</p>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            disabled={!hasPlainRole()}
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700 disabled:opacity-50"
                        >
                            Create User
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Update User Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => setShowUpdateModal(false)}
                title={`Update ${selectedUser?.username || 'User'}`}
                size="md"
            >
                <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                            <input
                                type="text"
                                value={formData.username}
                                disabled
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                            <input
                                type="email"
                                value={formData.email_id}
                                disabled
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                            <input
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                            <select
                                value={formData.gender}
                                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            >
                                <option value="">Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                            <input
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                            <input
                                type="tel"
                                value={formData.phone_no}
                                onChange={(e) => setFormData({ ...formData, phone_no: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                        </div>
                    </div>

                    <RoleChecklist
                        roles={roles}
                        selected={formData.role_ids}
                        onChange={(ids) => setFormData((prev) => ({ ...prev, role_ids: ids }))}
                    />

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Update User
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowUpdateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default UsersPage;
